import React, { useEffect, useState } from 'react';
import api from '../api';
import { Bot, MessageSquare, Loader2, Lightbulb, AlertCircle, Send } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

const AICoachPage = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConv, setSelectedConv] = useState('');
  const [coaching, setCoaching] = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/coach/alerts');
        setAlerts(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const getCoaching = async (conversationId) => {
    if (!conversationId) { toast.error('Vui lòng chọn hội thoại'); return; }
    setCoachLoading(true);
    setCoaching(null);
    try {
      const res = await api.post('/coach/suggest', { conversation_id: parseInt(conversationId) });
      setCoaching(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Có lỗi xảy ra');
    } finally {
      setCoachLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent"></div></div>;
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-gray-900">AI Coach</h2>
        <p className="text-sm text-gray-500 mt-1">Gợi ý thông minh giúp nhân viên cải thiện chất lượng CSKH</p>
      </div>

      {/* Manual coaching request */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <Bot className="w-5 h-5 text-blue-600" />
          <h4 className="font-semibold text-gray-800">Yêu cầu coaching</h4>
        </div>
        <div className="flex gap-2">
          <input
            type="number"
            value={selectedConv}
            onChange={(e) => setSelectedConv(e.target.value)}
            placeholder="Nhập ID hội thoại..."
            className="input-field flex-1"
          />
          <button onClick={() => getCoaching(selectedConv)} disabled={coachLoading} className="btn-primary flex items-center gap-2">
            {coachLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Phân tích
          </button>
        </div>
      </div>

      {/* Coaching Result */}
      {coaching && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
          {coaching.suggested_reply && (
            <div className="card border-l-4 border-blue-500">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="w-4 h-4 text-blue-600" />
                <h4 className="font-semibold text-sm text-gray-800">Gợi ý trả lời</h4>
              </div>
              <p className="text-sm text-gray-700 bg-blue-50 p-3 rounded-xl">{coaching.suggested_reply}</p>
            </div>
          )}

          {coaching.tone_analysis && (
            <div className="card border-l-4 border-purple-500">
              <h4 className="font-semibold text-sm text-gray-800 mb-2">Phân tích giọng điệu</h4>
              <p className="text-sm text-gray-700">{coaching.tone_analysis}</p>
            </div>
          )}

          {coaching.improvement_tips && coaching.improvement_tips.length > 0 && (
            <div className="card border-l-4 border-green-500">
              <div className="flex items-center gap-2 mb-3">
                <Lightbulb className="w-4 h-4 text-green-600" />
                <h4 className="font-semibold text-sm text-gray-800">Mẹo cải thiện</h4>
              </div>
              <ul className="space-y-2">
                {coaching.improvement_tips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="w-5 h-5 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {coaching.similar_cases && coaching.similar_cases.length > 0 && (
            <div className="card border-l-4 border-orange-500">
              <h4 className="font-semibold text-sm text-gray-800 mb-3">Trường hợp tương tự đã giải quyết</h4>
              <div className="space-y-2">
                {coaching.similar_cases.map((c, i) => (
                  <div key={i} className="p-3 bg-orange-50 rounded-xl text-sm text-gray-700">{c}</div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Auto-detected alerts */}
      {alerts.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-2 mb-4">
            <AlertCircle className="w-5 h-5 text-orange-500" />
            <h4 className="font-semibold text-gray-800">Hội thoại cần coaching</h4>
          </div>
          <div className="space-y-2">
            {alerts.map((alert, i) => (
              <button
                key={i}
                onClick={() => { setSelectedConv(String(alert.conversation_id)); getCoaching(alert.conversation_id); }}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors text-left"
              >
                <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                  <AlertCircle className="w-4 h-4 text-orange-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">Hội thoại #{alert.conversation_id}</p>
                  <p className="text-xs text-gray-400 truncate">{alert.reason}</p>
                </div>
                <span className="badge-negative">{alert.severity || 'Cần xem'}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default AICoachPage;
